import React, { useEffect, useRef, useState } from "react";
import {
  LocationCreateUpdateDto,
  LocationResponseDto,
} from "../common/ObjectTypes";
import { useProfile } from "../common/ProfileProvider";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocationSearchQueryType } from "../common/QueryTypes";
import { pageSize } from "../../api/apiUtils";
import {
  createLocation,
  getLocations,
  updateLocation,
} from "../../api/locationsApi";
import LocationsList from "../locations/LocationsList";
import LocationForm from "../locations/LocationForm";
import {
  maxLatitude,
  maxLongitude,
  minLatitude,
  minLongitude,
} from "../../constants";
import { getCountries } from "../../api/organizationsApi";
import i18n from "../../i18n";
import SearchBarLocations from "../locations/SearchBarLocations";
import tailwindClassnames from "../common/TailwindClassnames";
import ImportFromCsv from "../csvImport/ImportFromCsv";
import FollowImportUntilSettled from "../csvImport/FollowImportUntilSettled";
import type { CsvImportJobDto } from "../../api/migrationsApi";

type ManageProjectLocationsProps = {
  projectId: number;
  /**
   * The organisation the project belongs to (AKL-814). `ImportFromCsv` sends it as `org_id`
   * alongside `target_project_id`. `ManageProjectDetails` derives it — and `projectId` — with
   * `Number(...)` from state its project load fills, and still mounts this tab when that load
   * fails, so either can be NaN; the import entry points are withheld unless both are real
   * numbers.
   */
  orgId: number;
  perms: any;
};

const ManageProjectLocations: React.FC<ManageProjectLocationsProps> = ({
  projectId,
  orgId,
  perms,
}) => {
  const { t } = useTranslation();
  const { profile, profileLoading } = useProfile();
  const [locations, setLocations] = useState<LocationResponseDto[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const emptyLocation = {
    location_name: "",
    project_id: projectId,
    latitude: null,
    longitude: null,
    location_id: null,
    country_id: "",
  };
  const [countries, setCountries] = useState<string[]>([]);
  const [location, setLocation] = useState<any>(emptyLocation);
  const [originalLocation, setOriginalLocation] = useState<any>(emptyLocation);
  const [existingLocation, setExistingLocation] = useState<boolean>(false);
  const [canModifyLocation, setCanModifyLocation] = useState(false);
  const [canAddLocation, setCanAddLocation] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Imports the dialog let go of while the server was still applying them. The dialog's
  // own poller stops with the dialog, so the tab follows each one until it settles and
  // then refreshes the list — otherwise an apply that finishes after the close leaves the
  // list stale (PR #761 review). A list, not a single slot: letting go of a second job must
  // not stop following the first.
  const [pendingImportGuids, setPendingImportGuids] = useState<string[]>([]);
  // The import is offered only with the create permission AND real ids for both the
  // organisation and the project: the project page mounts this tab with NaN for both when
  // it could not load its project, and `ImportFromCsv` sends them as `org_id` and
  // `target_project_id` — NaN serialises as null and the API refuses the upload.
  const canImport =
    canAddLocation && Number.isFinite(orgId) && Number.isFinite(projectId);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [savingAndAddAnother, setSavingAndAddAnother] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [locationsSearchQuery, setLocationsSearchQuery] =
    useState<LocationSearchQueryType>({
      project_id: projectId,
      sort_by: "location_name",
      sort_direction: "asc",
      skip: 0,
      take: pageSize,
    });

  /*
   * Fetch locations when skip changes.
   */
  useEffect(() => {
    const getLocs = async () => {
      await fetchLocations();
    };
    getLocs();
  }, [
    locationsSearchQuery.skip,
    locationsSearchQuery.sort_by,
    locationsSearchQuery.sort_direction,
    locationsSearchQuery.country_id,
  ]);

  /*
   * Load locations on initial render and when projectId changes or profile loads.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const isAdmin = profile?.is_admin;
      const hasPermissionToModifyLocations = perms.some((perm: any) =>
        perm.name.includes("EDIT_DEPLOYMENTS_LOCATIONS"),
      );
      const hasPermissionToCreateLocations = perms.some((perm: any) =>
        perm.name.includes("CREATE_DEPLOYMENTS_LOCATIONS"),
      );

      setCanModifyLocation(isAdmin || hasPermissionToModifyLocations);
      setCanAddLocation(isAdmin || hasPermissionToCreateLocations);
    }
  }, [projectId, profile, profileLoading, perms]);

  /*
   * Load countries when language changes.
   */
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };

    loadCountries();
  }, [t]);

  // Ordinal of the most recent locations request. Refreshes can overlap — two import
  // followers settling close together, or a page change while a refresh is in flight — and
  // responses need not come back in order, so only the newest request may write the list;
  // an older response arriving late is dropped (PR #763 review).
  const latestLocationsRequest = useRef(0);

  /*
   * Fetch locations from the API. Only the most recently issued request updates the list.
   */
  const fetchLocations = async () => {
    const requestId = ++latestLocationsRequest.current;
    setListLoading(true);
    try {
      const temp = await getLocations(locationsSearchQuery);
      if (requestId !== latestLocationsRequest.current) return;
      setLocations(temp.locations);
      setTotalCount(temp.count);
    } catch (err) {
      if (requestId !== latestLocationsRequest.current) return;
      console.error("Error loading locations:", err);
    } finally {
      // A superseded request must not clear the loading state the newer one still owns.
      if (requestId === latestLocationsRequest.current) setListLoading(false);
    }
  };

  /*
   * A followed import has settled (or could not be checked on any more): stop following it
   * and reload the list once. `FollowImportUntilSettled` calls the latest copy of this, so
   * the refetch uses the current search query.
   */
  const handleImportSettled = (guid: string) => {
    setPendingImportGuids((current) => current.filter((g) => g !== guid));
    fetchLocations();
  };

  const openLocationModal = (locationExists: boolean) => {
    if (!locationExists) {
      setLocation(emptyLocation);
      setOriginalLocation(emptyLocation);
    }
    setExistingLocation(locationExists);
    setLocationModalOpen(true);
  };

  const closeLocationModal = () => {
    setLocationModalOpen(false);
    setErrors({});
    setSaveError("");
  };

  /*
   * Opens the CSV import (AKL-814). The Add Location modal is closed first: both overlays
   * sit at z-50 inside this tab's fixed wrappers, so leaving it open would stack two dialogs.
   */
  const openImportModal = () => {
    closeLocationModal();
    setImportOpen(true);
  };

  /*
   * An import was applied: reload the list. Called directly rather than through a
   * `skip: 0` update, which does not re-run the load effect when skip is already 0.
   */
  const handleImported = (_job: CsvImportJobDto) => {
    fetchLocations();
  };

  /*
   * The dialog is letting go of a job — on close, or when the user picks another file after
   * polling gave up. A job the server was still applying is followed from here until it
   * settles (see `pendingImportGuids`); nothing else needs following: a dry run writes
   * nothing, and a job already applied refreshed the list through `handleImported`.
   */
  const followIfStillApplying = (job: CsvImportJobDto | null) => {
    if (job?.status === "applying") {
      setPendingImportGuids((current) =>
        current.includes(job.guid) ? current : [...current, job.guid],
      );
    }
  };

  /*
   * The import dialog closed. A closed dialog cannot be reopened to the same job — no web
   * surface renders the project's migration history — so an apply still running is followed
   * from here. A job that had already been applied needs nothing: `handleImported`
   * refreshed the list the moment it reached that state, so refetching again on Done would
   * only repeat the request.
   */
  const handleImportClose = (lastJob: CsvImportJobDto | null) => {
    setImportOpen(false);
    followIfStillApplying(lastJob);
  };

  const requestSort = (sortby: string, sortdirection: "asc" | "desc") => {
    setLocationsSearchQuery((prevQuery) => ({
      ...prevQuery,
      sort_by: sortby,
      sort_direction: sortdirection,
      skip: 0,
    }));
  };

  const handleSelectLocation = (location: LocationResponseDto) => {
    setExistingLocation(true);
    setLocationLoading(true);
    openLocationModal(true);
    try {
      setLocation({
        ...location,
        latitude: Number(location.latitude).toFixed(6),
        longitude: Number(location.longitude).toFixed(6),
      });
      setOriginalLocation({
        ...location,
        latitude: Number(location.latitude).toFixed(6),
        longitude: Number(location.longitude).toFixed(6),
      });
      setLocationLoading(false);
    } catch (error) {
      console.error("Error selecting location:", error);
    }
  };

  const handleLocationFormChange = (e: any) => {
    setLocation((prevLocation: any) => ({
      ...prevLocation,
      [e.target.name]: e.target.value === "" ? null : e.target.value,
    }));
  };

  /*
   * Handle country selection in Add or Update Location form.
   */
  const handleSelectCountryInForm = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setLocation((prevState: any) => ({
        ...prevState,
        country_id: selectedOption.value,
      }));
    }
    if (!selectedOption) {
      setLocation((prevState: any) => ({
        ...prevState,
        country_id: "",
      }));
    }
  };

  /*
   * Handle search bar country selection.
   */
  const handleSelectCountryInSearchBar = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setLocationsSearchQuery((prevState) => ({
        ...prevState,
        country_id: selectedOption.value,
        skip: 0,
      }));
    }
    if (!selectedOption) {
      setLocationsSearchQuery((prevState) => ({
        ...prevState,
        country_id: "",
        skip: 0,
      }));
    }
  };

  /*
   * Handle search query change.
   */
  const handleSearchQueryChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setLocationsSearchQuery((prevQuery) => ({
      ...prevQuery,
      [e?.target?.name]: e?.target?.value,
    }));
  };

  /*
   * Retrieve locations with search parameters by fetching locations if skip is zero,
   * or setting skip to zero (which triggers fetching locations via a UseEffect).
   */
  const handleSearch = async () => {
    if (locationsSearchQuery.skip !== 0) {
      // reset skip to 0 when searching, trigger fetchDeployments
      setLocationsSearchQuery((prevQuery) => ({
        ...prevQuery,
        skip: 0,
      }));
    } else {
      await fetchLocations();
    }
  };
  const getLocationCoordinateErrors = (location: LocationCreateUpdateDto) => {
    let location_errors: any = {};
    if (location.latitude === null || location.latitude === undefined)
      location_errors.latitude = t("Latitude is required");
    if (location.longitude === null || location.longitude === undefined)
      location_errors.longitude = t("Longitude is required");

    if (
      location.latitude &&
      (location.latitude > maxLatitude || location.latitude < minLatitude)
    )
      location_errors.latitude =
        t("Latitude must be between ") +
        minLatitude +
        "° " +
        t("and ") +
        maxLatitude +
        "°";

    if (
      location.longitude &&
      (location.longitude > maxLongitude || location.longitude < minLongitude)
    )
      location_errors.longitude =
        t("Longitude must be between ") +
        minLongitude +
        "° " +
        t("and ") +
        maxLongitude +
        "°";
    setErrors(location_errors);
    return location_errors;
  };

  const formIsValid = () => {
    let form_errors: any = getLocationCoordinateErrors(location);
    if (!location.location_name || location.location_name === "")
      form_errors.location_name = t("Name is required");
    setErrors(form_errors);

    return Object.keys(form_errors).length === 0;
  };

  const getErrorMessage = (error: any, defaultMessage: string): string => {
    return error?.data?.detail?.toString() || defaultMessage;
  };

  const handleSave = async (addAnother: boolean) => {
    // Prevent duplicate submissions
    if (saving || savingAndAddAnother) return;

    if (!formIsValid()) return;

    // Clear any previous save errors
    setSaveError("");

    const validatedLocation: LocationCreateUpdateDto = {
      location_name: location.location_name,
      latitude: location.latitude as number,
      longitude: location.longitude as number,
      country_id: location.country_id as string,
      location_id: location.location_id,
      project_id: location.project_id,
    };
    if (existingLocation && validatedLocation.location_id !== null) {
      try {
        setSaving(true);
        await updateLocation(validatedLocation);
        await fetchLocations();
        closeLocationModal();
        toast.success(t("Location updated"));
      } catch (error: any) {
        const errorMessage = getErrorMessage(
          error,
          t("An error occurred while updating the location"),
        );
        setSaveError(t("Error updating location") + ": " + errorMessage);
      } finally {
        setSaving(false);
      }
    } else {
      try {
        if (addAnother) {
          setSavingAndAddAnother(true);
          await createLocation(validatedLocation);
          await fetchLocations();
          setLocation(emptyLocation);
          setOriginalLocation(emptyLocation);
          toast.success(t("Location added"));
        } else {
          setSaving(true);
          await createLocation(validatedLocation);
          await fetchLocations();
          closeLocationModal();
          toast.success(t("Location added"));
        }
      } catch (error: any) {
        const errorMessage = getErrorMessage(
          error,
          t("An error occurred while adding the location"),
        );
        setSaveError(t("Error adding location") + ": " + errorMessage);
      } finally {
        setSaving(false);
        setSavingAndAddAnother(false);
      }
    }
  };

  const handlePageClick = (e: any) => {
    setLocationsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
  };

  const handleCancel = () => {
    setErrors({});
    closeLocationModal();
  };

  return (
    <>
      {locationModalOpen && (
        <div className="fixed z-50">
          <LocationForm
            location={location}
            locationLoading={locationLoading}
            onClose={handleCancel}
            onChange={handleLocationFormChange}
            onSave={handleSave}
            onSelectCountry={handleSelectCountryInForm}
            errors={errors}
            saveError={saveError}
            countries={countries}
            saving={saving}
            savingAndAddAnother={savingAndAddAnother}
            existingLocation={existingLocation}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleSave(false);
            }}
            getLocationErrors={getLocationCoordinateErrors}
            viewOnly={!canModifyLocation}
            saveDisabled={
              JSON.stringify(location) === JSON.stringify(originalLocation)
            }
            onImportFromCsv={canImport ? openImportModal : undefined}
          />
        </div>
      )}
      {/* Never mounted without CREATE_DEPLOYMENTS_LOCATIONS: the API's 403 on the upload
          would be a dead end for the user (no longer a sign-out since AKL-939, but still
          a bundle chosen for nothing). A single Akili format — Wildlife
          Insights has no locations file — so the WI fork is never offered and the hand-off
          callback is a no-op. */}
      {canImport && importOpen && (
        <div className="fixed z-50">
          <ImportFromCsv
            isOpen={importOpen}
            orgId={orgId}
            entity="locations"
            formats={["akili_csv"]}
            targetProjectId={projectId}
            canMigrateFullExport={false}
            onClose={handleImportClose}
            onImported={handleImported}
            onMigrateFullExport={() => {}}
            onJobAbandoned={followIfStillApplying}
          />
        </div>
      )}
      {/* One follower per import still applying after its dialog let go of it; each has
          its own poller, so a second one never cancels the first. Render nothing visible. */}
      {pendingImportGuids.map((guid) => (
        <FollowImportUntilSettled
          key={guid}
          guid={guid}
          onSettled={handleImportSettled}
        />
      ))}
      {/* mx-8 keeps tab content aligned with the tab strip above it. */}
      <div className="mx-8 mt-6 mb-4">
        {/* SearchBarLocations + LocationsList render card-interior content —
            they expect this tableCard wrapper (filter toolbar attached to
            the table), matching the migrated OrganizationProjectsTab
            pattern. */}
        <div className={tailwindClassnames.tableCard}>
          <SearchBarLocations
            countries={countries}
            onCountrySelection={handleSelectCountryInSearchBar}
            canAddLocation={canAddLocation}
            openAddLocationModal={openLocationModal}
            onChangeSearchQuery={handleSearchQueryChange}
            onSearch={handleSearch}
            tempLocationsSearchQuery={locationsSearchQuery}
            onImportFromCsv={canImport ? openImportModal : undefined}
          />
          <LocationsList
            countries={countries}
            listLoading={listLoading}
            locations={locations}
            requestSort={requestSort}
            onSelectLocation={handleSelectLocation}
            onPageClicked={handlePageClick}
            take={locationsSearchQuery.take || pageSize}
            offset={locationsSearchQuery.skip ?? 0}
            totalCount={totalCount}
          />
        </div>
      </div>
    </>
  );
};

export default ManageProjectLocations;
